import { NgClass } from '@angular/common';
import { Component, DOCUMENT, ElementRef, EventEmitter, HostBinding, Input, OnChanges, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { WindowEventService } from '@frontend/sports/common/core/utils/window-event';
import { ParticipantImageComponent } from '@frontend/sports/participant-image/feature';

import { DropdownComponent } from '../../dropdown/dropdown.component';
import { DROPDOWN_CUSTOM_CLASS, DropdownOption, OptionType } from '../../dropdown/models';
import { DropdownImage, MultiColumnDropdownOption } from './scorecast-option-group.model';

@Component({
    selector: 'ms-multi-column-dropdown',
    templateUrl: './multi-column-dropdown.component.html',
    providers: [{ provide: DROPDOWN_CUSTOM_CLASS, useValue: 'multi-column-select' }],
    imports: [ParticipantImageComponent, NgClass, DropdownComponent],
})
export class MultiColumnDropdownComponent implements OnChanges {
    private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

    @Input() groups: MultiColumnDropdownOption[];
    @Input() active?: DropdownOption & { teamId?: number };
    @Input() any?: MultiColumnDropdownOption;
    @Input() images?: DropdownImage[];
    @Input() dropdownPlacement: 'right' | 'left' = 'left';
    @Input() textAlignment: 'left' | 'center';
    @Input() viewport: string | undefined;
    @Input() isFullWidth = false;

    @Output() optionChange = new EventEmitter<DropdownOption>();
    @Output() toggleDropdownState = new EventEmitter<boolean>();
    @HostBinding('class') className = 'multi-column';

    private readonly _doc = inject(DOCUMENT);

    options: MultiColumnDropdownOption[];
    imagesMap: Record<string, DropdownImage>;

    get header(): MultiColumnDropdownOption {
        return this.options[0];
    }

    readonly empty: MultiColumnDropdownOption = {
        id: 'empty',
        name: '',
        disabled: true,
        type: OptionType.Regular,
        payload: [],
    };

    constructor() {
        const windowResize = inject(WindowEventService);

        windowResize.resize
            .asObservable()
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.setSelectWidth());
    }

    optionChanged(option: DropdownOption): void {
        if (!option.disabled) {
            this.optionChange.emit(option);
        }
    }

    ngOnChanges(): void {
        this.options = [];
        this.imagesMap = {};

        if (!this.viewport) {
            this.viewport = 'body';
        }

        this.images?.forEach((image) => {
            this.imagesMap[image.id] = image;
        });

        const totalRows = Math.max(...this.groups.map((group) => group.payload.length));

        this.options.push({
            id: 'header',
            name: '',
            type: OptionType.Regular,
            payload: this.groups.map((header) => ({
                ...header,
                type: OptionType.Regular,
                disabled: true,
                payload: [],
            })),
        });

        for (let row = 0; row < totalRows; row++) {
            this.options.push({
                id: row,
                name: '',
                type: OptionType.Regular,
                payload: this.groups.map((group) => group.payload[row] ?? this.empty),
            });
        }

        if (this.any) {
            this.options.push({ ...this.any, type: OptionType.Regular, payload: [this.any] });
        }
    }

    toggleDropdown(isExpanded: boolean): void {
        if (isExpanded) {
            this.setSelectWidth();
        }

        this.toggleDropdownState.emit(isExpanded);
    }

    private setSelectWidth(): void {
        this._doc.body.style.setProperty(
            '--multi-column-select-width',
            `${this.elementRef.nativeElement.offsetWidth + (this.isFullWidth ? 0 : 8)}px`,
        );
    }
}
